import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  CRow,
  CCol,
  CCard,
  CCardHeader,
  CCardBody,
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormSelect,
  CFormInput,
} from "@coreui/react";
import CustomTimeline from "react-fast-timeline";
import "react-fast-timeline/style.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fromFakeLocalToUtcIso } from "../utils/timeUtils";
import axios from "axios";
import { toast } from "react-toastify";

const CameraTimelineSection = ({
  t,
  dateRangeForVis,
  timelineDataForVis = [],
  timelineTrucksForVis = [],
  timelineTwinForVis = [],
  useDigitalTwin = false,
  handleTimeDoubleClick,
  handleTwinCycleDoubleClick,
  device,
  currentTimeMs,
  trucksList = [],
  refreshAnalytics,
  setAnalyticsData,
  isStaff = false,
}) => {
  const [isSynced, setIsSynced] = useState(true);

  const [bounds1, setBounds1] = useState({ start: null, end: null });
  const [bounds2, setBounds2] = useState({ start: null, end: null });
  const isZoomedRef = useRef(false);
  const [selectedTruckSessionIds, setSelectedTruckSessionIds] = useState([]);
  const [isMerging, setIsMerging] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Модалка редактирования (Long-Press)
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editTruckId, setEditTruckId] = useState("unidentified");
  const [editCycles, setEditCycles] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);

  // Модалка ручного создания
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createStart, setCreateStart] = useState(new Date());
  const [createEnd, setCreateEnd] = useState(new Date());
  const [createTruckId, setCreateTruckId] = useState("unidentified");
  const [createCycles, setCreateCycles] = useState(5);
  const [savingCreate, setSavingCreate] = useState(false);

  useEffect(() => {
    if (dateRangeForVis && !isZoomedRef.current) {
      const initBounds = {
        start: dateRangeForVis.start.getTime(),
        end: dateRangeForVis.end.getTime(),
      };
      setBounds1(initBounds);
      setBounds2(initBounds);
    }
  }, [dateRangeForVis]);

  useEffect(() => {
    if (isSynced && bounds1.start !== null) {
      setBounds2(bounds1);
    }
  }, [isSynced, bounds1]);

  const handleViewChange1 = (start, end) => {
    isZoomedRef.current = true;
    setBounds1({ start, end });
    if (isSynced) setBounds2({ start, end });
  };

  const handleViewChange2 = (start, end) => {
    isZoomedRef.current = true;
    setBounds2({ start, end });
    if (isSynced) setBounds1({ start, end });
  };

  const getMarkerStyle = () => {
    if (!currentTimeMs || !bounds1.start || !bounds1.end) return { display: "none" };
    const totalViewMs = bounds1.end - bounds1.start;
    const offsetMs = currentTimeMs - bounds1.start;
    const percent = (offsetMs / totalViewMs) * 100;
    if (percent < 0 || percent > 100) return { display: "none" };

    return {
      left: `${percent}%`,
      position: "absolute",
      top: "25px",
      bottom: "0",
      width: "2px",
      backgroundColor: "#e55353",
      zIndex: 100,
      pointerEvents: "none",
      boxShadow: "0 0 6px rgba(229, 83, 83, 0.8)",
    };
  };

  const handleSelectionChange = (newSelectedIds) => {
    const validIds = newSelectedIds.filter((id) => !String(id).startsWith("wait_"));

    if (validIds.length <= 1) {
      setSelectedTruckSessionIds(validIds);
      return;
    }

    if (validIds.length > 2) {
      toast.warn("Можно объединить не более 2 заездов одновременно.");
      return;
    }

    const realTruckItems = timelineTrucksForVis.filter((it) => !String(it.id).startsWith("wait_"));
    const idx1 = realTruckItems.findIndex((it) => it.id === validIds[0]);
    const idx2 = realTruckItems.findIndex((it) => it.id === validIds[1]);

    if (idx1 !== -1 && idx2 !== -1) {
      const isNeighbor = Math.abs(idx1 - idx2) === 1;
      if (!isNeighbor) {
        toast.warn("Можно выбирать только соседние заезды!");
        return;
      }
    }

    setSelectedTruckSessionIds(validIds);
  };

  // --- УДАЛЕНИЕ ЗАЕЗДА ---
  const handleDeleteSession = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm(`Вы уверены, что хотите удалить заезд #${sessionId}?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/analytics/sessions/${sessionId}/details/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Заезд #${sessionId} успешно удален`);
      setEditModalVisible(false);
      setSelectedTruckSessionIds((prev) => prev.filter((id) => id !== sessionId));

      if (setAnalyticsData) {
        setAnalyticsData((prev) => {
          if (!prev || !prev.truck_sessions) return prev;
          const updatedSessions = prev.truck_sessions.filter((s) => s.id !== sessionId);
          return { ...prev, truck_sessions: updatedSessions };
        });
      }

      if (refreshAnalytics) {
        refreshAnalytics();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при удалении заезда");
    } finally {
      setIsDeleting(false);
    }
  };

  // --- ОБЪЕДИНЕНИЕ ЗАЕЗДОВ ---
  const handleMergeSubmit = async () => {
    if (selectedTruckSessionIds.length !== 2) return;

    const [id1, id2] = selectedTruckSessionIds;
    setIsMerging(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/analytics/sessions/merge/",
        { session_ids: [id1, id2] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Заезды успешно объединены");

      if (setAnalyticsData) {
        setAnalyticsData((prev) => {
          if (!prev || !prev.truck_sessions) return prev;

          const s1 = prev.truck_sessions.find((s) => s.id === id1);
          const s2 = prev.truck_sessions.find((s) => s.id === id2);
          if (!s1 || !s2) return prev;

          const newStart = new Date(Math.min(new Date(s1.start), new Date(s2.start))).toISOString();
          const newEnd = new Date(Math.max(new Date(s1.end), new Date(s2.end))).toISOString();
          const mergedCycles = (s1.cycles || 0) + (s2.cycles || 0);

          const targetTruckLabel = s1.label.includes("Неизвестная") ? s2.label : s1.label;
          const targetColor = s1.color || s2.color;
          const targetTruckId = s1.truck_id || s2.truck_id;

          const updatedSessions = prev.truck_sessions
            .filter((s) => s.id !== id2)
            .map((s) => {
              if (s.id === id1) {
                return {
                  ...s,
                  start: newStart,
                  end: newEnd,
                  cycles: mergedCycles,
                  label: targetTruckLabel,
                  color: targetColor,
                  truck_id: targetTruckId,
                };
              }
              return s;
            });

          return { ...prev, truck_sessions: updatedSessions };
        });
      }

      setSelectedTruckSessionIds([]);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при объединении");
    } finally {
      setIsMerging(false);
    }
  };

  const handleItemLongPress = ({ item }) => {
    if (!item || item.originalItem?.type === "wait" || String(item.id).startsWith("wait_")) {
      return;
    }

    const original = item.originalItem || {};
    let initialTruckId = original.truck_id || original.truck?.id || "";

    if (!initialTruckId && original.label) {
      const matched = trucksList.find(
        (tr) => `${tr.brand} (${tr.plate_number})` === original.label
      );
      if (matched) initialTruckId = matched.id;
    }

    setEditingSession(item);
    setEditTruckId(initialTruckId || "unidentified");
    setEditCycles(original.cycles || 0);
    setEditModalVisible(true);
  };

  const openCreateModal = () => {
    let baseTimeMs = currentTimeMs || bounds1.start || Date.now();
    const fakeStart = new Date(baseTimeMs);
    const fakeEnd = new Date(baseTimeMs + 3 * 60 * 1000);

    setCreateStart(fakeStart);
    setCreateEnd(fakeEnd);
    setCreateTruckId("unidentified");
    setCreateCycles(5);
    setCreateModalVisible(true);
  };

  const handleSaveCreateSubmit = async () => {
    if (!createStart || !createEnd || createStart >= createEnd) {
      toast.error("Неверные временные рамки");
      return;
    }
    setSavingCreate(true);
    try {
      const token = localStorage.getItem("token");
      const utcStart = fromFakeLocalToUtcIso(createStart, device.timezone_offset);
      const utcEnd = fromFakeLocalToUtcIso(createEnd, device.timezone_offset);

      await axios.post(
        `/api/analytics/sessions/create/`,
        {
          device_id: device.id,
          start_time: utcStart,
          end_time: utcEnd,
          truck_id: createTruckId === "unidentified" ? null : createTruckId,
          cycles: createCycles
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Заезд успешно создан!");
      setCreateModalVisible(false);
      if (refreshAnalytics) refreshAnalytics();
    } catch(err) {
      toast.error(err.response?.data?.error || "Ошибка создания");
    } finally {
      setSavingCreate(false);
    }
  };

  const handleSaveModalSubmit = async () => {
    if (!editingSession) return;
    setSavingEdit(true);

    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `/api/analytics/sessions/${editingSession.id}/details/`,
        {
          truck_id: editTruckId === "unidentified" ? null : editTruckId,
          target_cycles: editCycles,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Параметры заезда обновлены!");
      setEditModalVisible(false);

      if (setAnalyticsData) {
        setAnalyticsData((prev) => {
          if (!prev || !prev.truck_sessions) return prev;

          const selectedTruck = trucksList.find((tr) => String(tr.id) === String(editTruckId));
          const newLabel = selectedTruck
            ? `${selectedTruck.brand} (${selectedTruck.plate_number}) 🛠️`
            : "Неизвестная машина 🛠️";
          const newColor = selectedTruck ? selectedTruck.color || "#2eb85c" : "#17a2b8";
          const newCyclesNum = Number(editCycles) || 0;

          const updatedSessions = prev.truck_sessions.map((s) => {
            if (s.id === editingSession.id) {
              return {
                ...s,
                truck_id: selectedTruck ? selectedTruck.id : null,
                label: newLabel,
                color: newColor,
                cycles: newCyclesNum,
                determination_method: "manual",
              };
            }
            return s;
          });

          return { ...prev, truck_sessions: updatedSessions };
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSavingEdit(false);
    }
  };

  const showTrucksTimeline = timelineTrucksForVis.length > 0 && !device?.hide_truck_stats;

  return (
    <CRow className="mb-4">
      <CCol xs={12}>
        <CCard className="shadow-sm border-0">
          <CCardHeader className="bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
            <span>{t("analysis.timeline_title", "Временная шкала работы")}</span>

            {isStaff && (
              <div className="d-flex align-items-center gap-2">
                {selectedTruckSessionIds.length === 1 && (
                  <>
                    <CButton
                      color="danger"
                      size="sm"
                      className="text-white fw-bold"
                      disabled={isDeleting}
                      onClick={() => handleDeleteSession(selectedTruckSessionIds[0])}
                    >
                      {isDeleting ? "Удаление..." : "Удалить заезд"}
                    </CButton>
                    <CButton color="light" size="sm" onClick={() => setSelectedTruckSessionIds([])}>
                      Сброс
                    </CButton>
                    <div className="vr mx-1"></div>
                  </>
                )}

                {selectedTruckSessionIds.length === 2 && (
                  <>
                    <span className="small text-muted">
                      Выбрано: <b>2 / 2</b>
                    </span>
                    <CButton
                      color="primary"
                      size="sm"
                      disabled={isMerging}
                      onClick={handleMergeSubmit}
                    >
                      {isMerging ? "Объединение..." : "Объединить заезды"}
                    </CButton>
                    <CButton color="light" size="sm" onClick={() => setSelectedTruckSessionIds([])}>
                      Сброс
                    </CButton>
                    <div className="vr mx-1"></div>
                  </>
                )}
                
                <CButton color="success" size="sm" className="text-white fw-bold" onClick={openCreateModal}>
                  + Создать заезд
                </CButton>
              </div>
            )}
          </CCardHeader>

          <CCardBody>
            {dateRangeForVis && bounds1.start !== null ? (
              <div className="position-relative">
                <div className="d-flex justify-content-between align-items-end mb-2">
                  <div className="fw-bold text-muted small">
                    {t("analysis.excavator_work", "Работа экскаватора")}
                  </div>

                  {showTrucksTimeline && (
                    <label
                      style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", margin: 0 }}
                      className="text-muted small"
                    >
                      <input
                        type="checkbox"
                        checked={isSynced}
                        onChange={(e) => setIsSynced(e.target.checked)}
                      />
                      Синхронизировать прокрутку
                    </label>
                  )}
                </div>

                {/* ОВЕРЛЕЙ ПЕРЕРЫВОВ */}
                {timelineDataForVis
                  .filter((item) => item.originalItem?.class_id === -2 || item.originalItem?.class_id === -3)
                  .map((brk, idx) => {
                    if (!bounds1.start || !bounds1.end) return null;
                    const totalViewMs = bounds1.end - bounds1.start;
                    const offsetMs = brk.start - bounds1.start;
                    const durationMs = brk.end - brk.start;
                    const leftPct = (offsetMs / totalViewMs) * 100;
                    const rightPct = ((offsetMs + durationMs) / totalViewMs) * 100;
                    const renderLeft = Math.max(0, leftPct);
                    const renderRight = Math.min(100, rightPct);
                    const renderWidth = renderRight - renderLeft;
                    if (renderWidth <= 0) return null;

                    const isMaintenance = brk.originalItem.class_id === -2;
                    const title = isMaintenance ? "ТО / Выходной" : "Перерыв";

                    return (
                      <div
                        key={`break-overlay-${idx}`}
                        title={title}
                        style={{
                          position: "absolute",
                          top: "25px",
                          bottom: "0",
                          left: `${renderLeft}%`,
                          width: `${renderWidth}%`,
                          pointerEvents: "none",
                          zIndex: 5,
                          background:
                            "repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(0,0,0,0.1) 5px, rgba(0,0,0,0.1) 10px)",
                          backgroundColor: "rgba(200, 200, 200, 0.25)",
                          borderLeft: renderLeft > 0 ? "1px dashed #666" : "none",
                          borderRight: renderRight < 100 ? "1px dashed #666" : "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {renderWidth > 3 && (
                          <span
                            style={{
                              color: "#555",
                              fontWeight: "bold",
                              fontSize: "0.85rem",
                              textShadow: "1px 1px 0 #fff",
                              whiteSpace: "nowrap",
                              transform: "rotate(-90deg)",
                            }}
                          >
                            {title}
                          </span>
                        )}
                      </div>
                    );
                  })}

                {/* КРАСНЫЙ МАРКЕР ВРЕМЕНИ */}
                <div style={getMarkerStyle()}>
                  <div
                    style={{
                      position: "absolute",
                      top: "-6px",
                      left: "-5px",
                      width: "12px",
                      height: "12px",
                      backgroundColor: "#e55353",
                      borderRadius: "50%",
                      border: "2px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  ></div>
                </div>

                {/* ТАЙМЛАЙН ЦИФРОВОГО ДВОЙНИКА */}
                {useDigitalTwin && (
                  <div className="mb-4 p-2 bg-light border rounded">
                    <div className="fw-bold text-primary small mb-2 d-flex align-items-center">
                      <span className="me-2">🤖</span> Цифровой двойник AI (Циклы с объемами): {timelineTwinForVis.length} шт.
                    </div>
                    {timelineTwinForVis.length > 0 ? (
                      <CustomTimeline
                        items={timelineTwinForVis}
                        initialStart={bounds1.start}
                        initialEnd={bounds1.end}
                        onViewChange={handleViewChange1}
                        onTimeDoubleClick={handleTwinCycleDoubleClick}
                      />
                    ) : (
                      <div className="p-2 text-muted small text-center">
                        Циклы двойника в процессе расчета...
                      </div>
                    )}
                  </div>
                )}

                {/* ТАЙМЛАЙН ЭКСКАВАТОРА */}
                {timelineDataForVis.length > 0 && (
                  <CustomTimeline
                    items={timelineDataForVis.filter(
                      (item) => item.originalItem?.class_id !== -2 && item.originalItem?.class_id !== -3
                    )}
                    initialStart={bounds1.start}
                    initialEnd={bounds1.end}
                    onViewChange={handleViewChange1}
                    onTimeDoubleClick={handleTimeDoubleClick}
                  />
                )}

                {/* ТАЙМЛАЙН САМОСВАЛОВ */}
                {showTrucksTimeline && (
                  <>
                    <div className="mt-4 mb-2 d-flex justify-content-between align-items-center">
                      <div className="fw-bold text-muted small">
                        {t("analysis.truck_loading", "Загрузка самосвалов")}
                      </div>
                      {isStaff && (
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          💡 <i>Клик — выбрать заезд для удаления (1) или объединения (2). Зажатие ЛКМ — редактировать.</i>
                        </div>
                      )}
                    </div>

                    <CustomTimeline
                      items={timelineTrucksForVis}
                      initialStart={bounds2.start}
                      initialEnd={bounds2.end}
                      onViewChange={handleViewChange2}
                      onTimeDoubleClick={handleTimeDoubleClick}
                      selectable={isStaff}
                      selectionRule="free"
                      selectedIds={selectedTruckSessionIds}
                      onSelectionChange={handleSelectionChange}
                      enableLongPress={isStaff}
                      longPressDelay={800}
                      onItemLongPress={handleItemLongPress}
                    />
                  </>
                )}
              </div>
            ) : (
              <div className="text-center p-3 text-muted">
                Выберите период и нажмите "Получить аналитику"
              </div>
            )}
          </CCardBody>
        </CCard>
      </CCol>

      {/* МОДАЛКА РЕДАКТИРОВАНИЯ ЗАЕЗДА С КНОПКОЙ УДАЛЕНИЯ */}
      <CModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        alignment="center"
        size="sm"
      >
        <CModalHeader closeButton className="bg-light">
          <CModalTitle className="fs-6 fw-bold text-primary">
            Редактирование заезда #{editingSession?.id}
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="p-3">
          <div className="mb-3">
            <label className="form-label small fw-semibold text-muted">Самосвал:</label>
            <CFormSelect
              size="sm"
              value={editTruckId}
              onChange={(e) => setEditTruckId(e.target.value)}
            >
              <option value="unidentified">Неизвестная машина</option>
              {trucksList.map((tr) => (
                <option key={tr.id} value={tr.id}>
                  {tr.brand} ({tr.plate_number})
                </option>
              ))}
            </CFormSelect>
          </div>

          <div className="mb-2">
            <label className="form-label small fw-semibold text-muted">Количество ковшей:</label>
            <CFormInput
              type="number"
              size="sm"
              min="1"
              max="100"
              value={editCycles}
              onChange={(e) => setEditCycles(e.target.value)}
            />
          </div>
        </CModalBody>
        <CModalFooter className="bg-light py-2 d-flex justify-content-between">
          <CButton
            color="danger"
            variant="ghost"
            size="sm"
            className="text-danger fw-bold"
            disabled={savingEdit || isDeleting}
            onClick={() => handleDeleteSession(editingSession?.id)}
          >
            {isDeleting ? "Удаление..." : "Удалить заезд"}
          </CButton>
          
          <div className="d-flex gap-2">
            <CButton
              color="secondary"
              variant="ghost"
              size="sm"
              onClick={() => setEditModalVisible(false)}
            >
              Отмена
            </CButton>
            <CButton
              color="primary"
              size="sm"
              disabled={savingEdit || isDeleting}
              onClick={handleSaveModalSubmit}
            >
              {savingEdit ? "Сохранение..." : "Сохранить"}
            </CButton>
          </div>
        </CModalFooter>
      </CModal>

      {/* МОДАЛКА СОЗДАНИЯ ЗАЕЗДА */}
      <CModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        alignment="center"
        size="sm"
      >
        <CModalHeader closeButton className="bg-light">
          <CModalTitle className="fs-6 fw-bold text-success">
            Ручное создание заезда
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="p-3">
          <div className="mb-3">
            <label className="form-label small fw-semibold text-muted">Самосвал:</label>
            <CFormSelect
              size="sm"
              value={createTruckId}
              onChange={(e) => setCreateTruckId(e.target.value)}
            >
              <option value="unidentified">Неизвестная машина</option>
              {trucksList.map((tr) => (
                <option key={tr.id} value={tr.id}>
                  {tr.brand} ({tr.plate_number})
                </option>
              ))}
            </CFormSelect>
          </div>

          <div className="mb-3">
            <label className="form-label small fw-semibold text-muted">Начало:</label>
            <DatePicker
              selected={createStart}
              onChange={(d) => setCreateStart(d)}
              showTimeSelect
              dateFormat="dd.MM.yyyy HH:mm:ss"
              timeFormat="HH:mm:ss"
              className="form-control form-control-sm"
            />
          </div>

          <div className="mb-3">
            <label className="form-label small fw-semibold text-muted">Конец:</label>
            <DatePicker
              selected={createEnd}
              onChange={(d) => setCreateEnd(d)}
              showTimeSelect
              dateFormat="dd.MM.yyyy HH:mm:ss"
              timeFormat="HH:mm:ss"
              className="form-control form-control-sm"
            />
          </div>

          <div className="mb-2">
            <label className="form-label small fw-semibold text-muted">Количество ковшей:</label>
            <CFormInput
              type="number"
              size="sm"
              min="1"
              max="100"
              value={createCycles}
              onChange={(e) => setCreateCycles(e.target.value)}
            />
          </div>
        </CModalBody>
        <CModalFooter className="bg-light py-2">
          <CButton
            color="secondary"
            variant="ghost"
            size="sm"
            onClick={() => setCreateModalVisible(false)}
          >
            Отмена
          </CButton>
          <CButton
            color="success"
            size="sm"
            className="text-white fw-bold"
            disabled={savingCreate}
            onClick={handleSaveCreateSubmit}
          >
            {savingCreate ? "Создание..." : "Создать"}
          </CButton>
        </CModalFooter>
      </CModal>
    </CRow>
  );
};

CameraTimelineSection.propTypes = {
  t: PropTypes.func.isRequired,
  dateRangeForVis: PropTypes.object,
  timelineDataForVis: PropTypes.array,
  timelineTrucksForVis: PropTypes.array,
  timelineTwinForVis: PropTypes.array,
  useDigitalTwin: PropTypes.bool,
  handleTimeDoubleClick: PropTypes.func,
  handleTwinCycleDoubleClick: PropTypes.func,
  device: PropTypes.object,
  currentTimeMs: PropTypes.number,
  trucksList: PropTypes.array,
  refreshAnalytics: PropTypes.func,
  setAnalyticsData: PropTypes.func,
  isStaff: PropTypes.bool,
};

export default CameraTimelineSection;